import React, { useState, useEffect, useMemo } from 'react';
import {
    Send,
    Bell,
    Users,
    Plus,
    FileText,
    CheckSquare,
    AlertTriangle,
    CheckCircle2,
    Clock,
    Mail,
    MessageSquare,
    Smartphone,
    TrendingUp,
    ChevronRight,
    Calendar,
    Activity,
    ShieldAlert,
    Info,
    List,
    Building2
} from 'lucide-react';
import { getAuthorizedHeaders } from '../utils/authorizedHeaders';

const API_BASE = 'https://warngen-api-352514293915.europe-west1.run.app';

const Dashboard = ({
    contactos = [],
    plantillas = [],
    listas = [],
    usuario,
    clienteActivo,
    setVista,
    iniciarFlujoAlerta
}) => {
    const [alertas, setAlertas] = useState([]);
    const [logs, setLogs] = useState([]);
    const [pendientesAprobacion, setPendientesAprobacion] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [periodoDias, setPeriodoDias] = useState(30);
    const [ahora, setAhora] = useState(new Date());

    // Reloj en vivo en la cabecera
    useEffect(() => {
        const timer = setInterval(() => setAhora(new Date()), 30000);
        return () => clearInterval(timer);
    }, []);

    // Carga de datos reales del backend
    useEffect(() => {
        let cancelado = false;
        const cargar = async () => {
            setCargando(true);
            try {
                const peticiones = [
                    fetch(`${API_BASE}/api/informes/alertas`, { headers: getAuthorizedHeaders() }),
                    fetch(`${API_BASE}/api/informes/logs`, { headers: getAuthorizedHeaders() })
                ];
                if (usuario?.rol !== 'operador') {
                    peticiones.push(
                        fetch(`${API_BASE}/api/enviar/pendientes`, { headers: getAuthorizedHeaders() })
                    );
                }

                const respuestas = await Promise.allSettled(peticiones);

                if (!cancelado) {
                    if (respuestas[0]?.status === 'fulfilled' && respuestas[0].value.ok) {
                        const dAle = await respuestas[0].value.json();
                        setAlertas(Array.isArray(dAle) ? dAle : []);
                    }
                    if (respuestas[1]?.status === 'fulfilled' && respuestas[1].value.ok) {
                        const dLogs = await respuestas[1].value.json();
                        setLogs(Array.isArray(dLogs) ? dLogs : []);
                    }
                    if (respuestas[2]?.status === 'fulfilled' && respuestas[2].value.ok) {
                        const dPend = await respuestas[2].value.json();
                        setPendientesAprobacion(Array.isArray(dPend) ? dPend : []);
                    }
                }
            } catch (err) {
                console.error('Error cargando datos del dashboard:', err);
            } finally {
                if (!cancelado) setCargando(false);
            }
        };
        cargar();
        return () => { cancelado = true; };
    }, [usuario]);

    // Mapa rápido plantilla_nombre -> tipo_alerta_codigo
    const mapaPlantillaTipo = useMemo(() => {
        const map = {};
        plantillas.forEach(p => {
            if (p.nombre) map[p.nombre.toLowerCase()] = p.tipo_alerta_codigo || 'INFORMATIVA';
            if (p.id) map[p.id] = p.tipo_alerta_codigo || 'INFORMATIVA';
        });
        return map;
    }, [plantillas]);

    // Filtrar alertas por periodo seleccionado
    const alertasPeriodo = useMemo(() => {
        const limite = new Date();
        limite.setDate(limite.getDate() - periodoDias);
        return alertas.filter(a => {
            const f = new Date(a.fecha_enviada || a.fecha_creacion || Date.now());
            return f >= limite;
        });
    }, [alertas, periodoDias]);

    // Métricas principales (KPI Cards superiores)
    const metricas = useMemo(() => {
        const ahoraMs = Date.now();
        const hace7d = ahoraMs - 7 * 86400000;
        const hace14d = ahoraMs - 14 * 86400000;

        const totalComunicaciones = alertas.filter(a => a.estado === 'enviada').length;
        const comUlt7 = alertas.filter(a => {
            const t = new Date(a.fecha_enviada || a.fecha_creacion).getTime();
            return a.estado === 'enviada' && t >= hace7d;
        }).length;
        const comPrev7 = alertas.filter(a => {
            const t = new Date(a.fecha_enviada || a.fecha_creacion).getTime();
            return a.estado === 'enviada' && t >= hace14d && t < hace7d;
        }).length;

        const poblacionAlcanzada = alertas.reduce(
            (acc, a) => acc + (Number(a.destinatarios_exitosos) || 0),
            0
        );
        const alcUlt7 = alertas
            .filter(a => new Date(a.fecha_enviada || a.fecha_creacion).getTime() >= hace7d)
            .reduce((acc, a) => acc + (Number(a.destinatarios_exitosos) || 0), 0);

        const contactosActivos = contactos.filter(c => c.activo !== false && c.activo !== 0).length;

        // Alertas recientes (últimas 48h) o programadas
        const alertasActivas = alertas.filter(a => {
            const t = new Date(a.fecha_enviada || a.fecha_creacion).getTime();
            return a.estado === 'programada' || (a.estado === 'enviada' && ahoraMs - t <= 48 * 3600000);
        }).length;

        // Incidencias: envíos con fallos recientes + pendientes de aprobación
        const enviosConFallo = alertas.filter(
            a => a.estado === 'fallida' || Number(a.destinatarios_fallidos) > 0
        ).slice(0, 10).length;
        const incidenciasPendientes = pendientesAprobacion.length + enviosConFallo;

        return {
            alertasActivas,
            totalComunicaciones,
            comUlt7,
            comPrev7,
            poblacionAlcanzada,
            alcUlt7,
            contactosActivos,
            incidenciasPendientes,
            enviosConFallo
        };
    }, [alertas, contactos, pendientesAprobacion]);

    // Distribución por Tipo de Alerta (Donut Chart)
    const distribucionTipos = useMemo(() => {
        const conteo = {
            EMERGENCIA: { label: 'Emergencias / Críticas', count: 0, color: '#EF4444' },
            PRECAUCION: { label: 'Advertencias / Avisos', count: 0, color: '#F59E0B' },
            INFORMATIVA: { label: 'Información General', count: 0, color: '#3B82F6' },
            EVENTOS: { label: 'Campañas / Otros', count: 0, color: '#10B981' }
        };

        const fuente = alertasPeriodo.length > 0 ? alertasPeriodo : alertas;

        fuente.forEach(a => {
            const nombreP = (a.plantilla_nombre || '').toLowerCase();
            const tipoCodigo = (mapaPlantillaTipo[nombreP] || '').toUpperCase();

            if (tipoCodigo.includes('EMERG') || tipoCodigo.includes('CRIT') || nombreP.includes('emergencia') || nombreP.includes('alerta')) {
                conteo.EMERGENCIA.count++;
            } else if (tipoCodigo.includes('PREC') || tipoCodigo.includes('ADV') || nombreP.includes('aviso') || nombreP.includes('seguridad')) {
                conteo.PRECAUCION.count++;
            } else if (tipoCodigo.includes('INFO') || nombreP.includes('bienvenida') || nombreP.includes('info')) {
                conteo.INFORMATIVA.count++;
            } else {
                conteo.EVENTOS.count++;
            }
        });

        const total = Object.values(conteo).reduce((s, i) => s + i.count, 0);
        return {
            total,
            items: Object.values(conteo).map(item => ({
                ...item,
                pct: total > 0 ? Math.round((item.count / total) * 100) : 0
            }))
        };
    }, [alertasPeriodo, alertas, mapaPlantillaTipo]);

    // Distribución por Canales de Comunicación (Barras horizontales)
    const estadisticasCanales = useMemo(() => {
        const fuente = alertasPeriodo.length > 0 ? alertasPeriodo : alertas;
        const canales = {
            email: {
                id: 'email',
                label: 'Correo electrónico',
                icon: Mail,
                colorBar: 'bg-indigo-500',
                colorText: 'text-indigo-600 dark:text-indigo-400',
                enviados: 0,
                total: 0,
                suscritos: contactos.filter(c => c.activo && c.email && c.suscrito_email).length
            },
            sms: {
                id: 'sms',
                label: 'SMS directo',
                icon: MessageSquare,
                colorBar: 'bg-blue-500',
                colorText: 'text-blue-600 dark:text-blue-400',
                enviados: 0,
                total: 0,
                suscritos: contactos.filter(c => c.activo && c.telefono && c.suscrito_sms).length
            },
            whatsapp: {
                id: 'whatsapp',
                label: 'WhatsApp Business',
                icon: Smartphone,
                colorBar: 'bg-emerald-500',
                colorText: 'text-emerald-600 dark:text-emerald-400',
                enviados: 0,
                total: 0,
                suscritos: contactos.filter(c => c.activo && (c.whatsapp || c.telefono) && c.suscrito_whatsapp).length
            }
        };

        fuente.forEach(a => {
            const c = (a.canal || '').toLowerCase();
            if (canales[c]) {
                canales[c].enviados += Number(a.destinatarios_exitosos) || 0;
                canales[c].total += Number(a.total_destinatarios) || 0;
            }
        });

        const maxEnviados = Math.max(1, ...Object.values(canales).map(c => c.enviados));

        return Object.values(canales).map(c => {
            const tasaEntrega = c.total > 0 ? Math.round((c.enviados / c.total) * 100) : 100;
            const anchoBarra = c.enviados > 0 ? Math.max(12, Math.round((c.enviados / maxEnviados) * 100)) : 8;
            return { ...c, tasaEntrega, anchoBarra };
        });
    }, [alertasPeriodo, alertas, contactos]);

    // Curva de evolución acumulada de Población Alcanzada (SVG Area Chart)
    const serieEvolucion = useMemo(() => {
        const puntosCount = 8;
        const ahoraMs = Date.now();
        const ventanaMs = periodoDias * 86400000;
        const inicioMs = ahoraMs - ventanaMs;
        const pasoMs = ventanaMs / (puntosCount - 1);

        const ordenadas = [...alertas]
            .filter(a => a.estado === 'enviada')
            .sort((a, b) => new Date(a.fecha_enviada || a.fecha_creacion) - new Date(b.fecha_enviada || b.fecha_creacion));

        const puntos = [];
        for (let i = 0; i < puntosCount; i++) {
            const corte = inicioMs + i * pasoMs;
            const fechaLabel = new Date(corte).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
            const acumulado = ordenadas
                .filter(a => new Date(a.fecha_enviada || a.fecha_creacion).getTime() <= corte)
                .reduce((acc, a) => acc + (Number(a.destinatarios_exitosos) || 0), 0);
            puntos.push({ label: fechaLabel, valor: acumulado });
        }
        return puntos;
    }, [alertas, periodoDias]);

    // Formateo de fecha actual estilo cabecera
    const fechaFormateada = ahora.toLocaleDateString('es-ES', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric'
    });
    const horaFormateada = ahora.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });

    // Cálculo de arcos SVG para el Donut Chart
    const radioDonut = 58;
    const circunferencia = 2 * Math.PI * radioDonut;
    let offsetAcumulado = 0;

    // Puntos SVG para la gráfica de línea / área
    const maxSerie = Math.max(10, ...serieEvolucion.map(p => p.valor));
    const anchoSvg = 460;
    const altoSvg = 150;
    const padX = 28;
    const padY = 20;
    const coords = serieEvolucion.map((pt, idx) => {
        const x = padX + (idx / Math.max(1, serieEvolucion.length - 1)) * (anchoSvg - padX * 2);
        const y = altoSvg - padY - (pt.valor / maxSerie) * (altoSvg - padY * 2);
        return { x, y, ...pt };
    });
    const polylinePoints = coords.map(c => `${c.x},${c.y}`).join(' ');
    const areaPoints = coords.length > 0
        ? `${coords[0].x},${altoSvg - padY} ${polylinePoints} ${coords[coords.length - 1].x},${altoSvg - padY}`
        : '';

    return (
        <div className="space-y-4 sm:space-y-6 pb-8">
            {/* BARRA SUPERIOR OPERATIVA */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/80 dark:border-gray-700 px-4 sm:px-6 py-4 flex flex-col lg:flex-row lg:items-center justify-between gap-3.5 sm:gap-4">
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                    <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold bg-emerald-50 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-300 border border-emerald-200 dark:border-emerald-800">
                        <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                        Sistema operativo
                    </span>

                    {clienteActivo && (
                        <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 max-w-full truncate">
                            <Building2 size={14} className="shrink-0" />
                            <span className="truncate">Entidad: <strong>{clienteActivo.nombre}</strong></span>
                        </span>
                    )}

                    <div className="hidden md:flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400 pl-2">
                        <span className="flex items-center gap-1.5 capitalize">
                            <Calendar size={14} className="text-gray-400" />
                            {fechaFormateada}
                        </span>
                        <span className="flex items-center gap-1 font-mono">
                            <Clock size={14} className="text-gray-400" />
                            {horaFormateada}
                        </span>
                    </div>
                </div>

                <div className="flex items-center gap-2.5 sm:gap-3 w-full sm:w-auto">
                    <button
                        onClick={() => setVista('contactos')}
                        className="flex-1 sm:flex-initial justify-center px-3 sm:px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 text-xs sm:text-sm font-medium transition flex items-center gap-1.5 sm:gap-2"
                    >
                        <Users size={16} className="shrink-0" />
                        <span className="truncate">Contactos ({metricas.contactosActivos.toLocaleString('es-ES')})</span>
                    </button>
                    <button
                        onClick={iniciarFlujoAlerta}
                        className="flex-1 sm:flex-initial justify-center bg-blue-600 hover:bg-blue-700 text-white px-4 sm:px-5 py-2.5 rounded-xl font-semibold text-xs sm:text-sm shadow-sm hover:shadow transition flex items-center gap-1.5 sm:gap-2"
                    >
                        <Plus size={18} className="shrink-0" />
                        <span>Crear alerta</span>
                    </button>
                </div>
            </div>

            {/* 4 TARJETAS KPI SUPERIORES (ESTILO IMAGEN) */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
                {/* KPI 1: Alertas recientes / activas */}
                <div
                    onClick={() => setVista('informes')}
                    className="bg-gradient-to-br from-rose-50/90 via-white to-white dark:from-rose-950/30 dark:via-gray-800 dark:to-gray-800 p-5 rounded-2xl shadow-sm border border-rose-100 dark:border-rose-900/50 hover:shadow-md transition cursor-pointer group"
                >
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3.5">
                            <div className="w-12 h-12 rounded-xl bg-rose-500 text-white flex items-center justify-center shadow-sm">
                                <AlertTriangle size={24} />
                            </div>
                            <div>
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Alertas recientes (48h)</p>
                                <p className="text-3xl font-extrabold text-gray-900 dark:text-gray-100 mt-0.5">
                                    {metricas.alertasActivas}
                                </p>
                            </div>
                        </div>
                        <ChevronRight size={18} className="text-gray-400 group-hover:translate-x-0.5 transition" />
                    </div>
                    <div className="mt-3 pt-2.5 border-t border-rose-100/80 dark:border-gray-700 flex items-center justify-between text-xs">
                        <span className="text-rose-600 dark:text-rose-400 font-semibold flex items-center gap-1">
                            <TrendingUp size={13} /> +{metricas.comUlt7} esta semana
                        </span>
                        <span className="text-gray-400">en seguimiento</span>
                    </div>
                </div>

                {/* KPI 2: Comunicaciones enviadas */}
                <div
                    onClick={() => setVista('informes')}
                    className="bg-gradient-to-br from-blue-50/90 via-white to-white dark:from-blue-950/30 dark:via-gray-800 dark:to-gray-800 p-5 rounded-2xl shadow-sm border border-blue-100 dark:border-blue-900/50 hover:shadow-md transition cursor-pointer group"
                >
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3.5">
                            <div className="w-12 h-12 rounded-xl bg-blue-600 text-white flex items-center justify-center shadow-sm">
                                <Send size={22} />
                            </div>
                            <div>
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Comunicaciones enviadas</p>
                                <p className="text-3xl font-extrabold text-gray-900 dark:text-gray-100 mt-0.5">
                                    {metricas.totalComunicaciones}
                                </p>
                            </div>
                        </div>
                        <ChevronRight size={18} className="text-gray-400 group-hover:translate-x-0.5 transition" />
                    </div>
                    <div className="mt-3 pt-2.5 border-t border-blue-100/80 dark:border-gray-700 flex items-center justify-between text-xs">
                        <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                            ↑ {metricas.comUlt7} últimos 7 días
                        </span>
                        <span className="text-gray-400">{plantillas.length} plantillas activas</span>
                    </div>
                </div>

                {/* KPI 3: Población / Destinatarios alcanzados */}
                <div
                    onClick={() => setVista('contactos')}
                    className="bg-gradient-to-br from-emerald-50/90 via-white to-white dark:from-emerald-950/30 dark:via-gray-800 dark:to-gray-800 p-5 rounded-2xl shadow-sm border border-emerald-100 dark:border-emerald-900/50 hover:shadow-md transition cursor-pointer group"
                >
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3.5">
                            <div className="w-12 h-12 rounded-xl bg-emerald-600 text-white flex items-center justify-center shadow-sm">
                                <Users size={24} />
                            </div>
                            <div>
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Población alcanzada</p>
                                <p className="text-3xl font-extrabold text-gray-900 dark:text-gray-100 mt-0.5">
                                    {metricas.poblacionAlcanzada.toLocaleString('es-ES')}
                                </p>
                            </div>
                        </div>
                        <ChevronRight size={18} className="text-gray-400 group-hover:translate-x-0.5 transition" />
                    </div>
                    <div className="mt-3 pt-2.5 border-t border-emerald-100/80 dark:border-gray-700 flex items-center justify-between text-xs">
                        <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                            {metricas.contactosActivos.toLocaleString('es-ES')} contactos en directorio
                        </span>
                        <span className="text-gray-400">{listas.length} listas</span>
                    </div>
                </div>

                {/* KPI 4: Incidencias / Acciones pendientes */}
                <div
                    onClick={() => setVista(pendientesAprobacion.length > 0 ? 'aprobaciones' : 'informes')}
                    className="bg-gradient-to-br from-amber-50/90 via-white to-white dark:from-amber-950/30 dark:via-gray-800 dark:to-gray-800 p-5 rounded-2xl shadow-sm border border-amber-100 dark:border-amber-900/50 hover:shadow-md transition cursor-pointer group"
                >
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3.5">
                            <div className="w-12 h-12 rounded-xl bg-amber-500 text-white flex items-center justify-center shadow-sm">
                                <CheckSquare size={22} />
                            </div>
                            <div>
                                <p className="text-xs font-medium text-gray-500 dark:text-gray-400">Incidencias / Pendientes</p>
                                <p className="text-3xl font-extrabold text-gray-900 dark:text-gray-100 mt-0.5">
                                    {metricas.incidenciasPendientes}
                                </p>
                            </div>
                        </div>
                        <ChevronRight size={18} className="text-gray-400 group-hover:translate-x-0.5 transition" />
                    </div>
                    <div className="mt-3 pt-2.5 border-t border-amber-100/80 dark:border-gray-700 flex items-center justify-between text-xs">
                        <span className="text-amber-700 dark:text-amber-400 font-semibold">
                            {pendientesAprobacion.length} por aprobar
                        </span>
                        <span className="text-gray-400">{metricas.enviosConFallo} con rebotes/fallos</span>
                    </div>
                </div>
            </div>

            {/* BLOQUE CENTRAL (SIN MAPA): ÚLTIMAS COMUNICACIONES + ACTIVIDAD Y ACCIONES PENDIENTES */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Columna Izquierda (7 cols): Últimas comunicaciones multicanal */}
                <div className="lg:col-span-7 bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/80 dark:border-gray-700 p-4 sm:p-6 flex flex-col justify-between">
                    <div>
                        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
                            <div>
                                <h3 className="text-base font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                                    <Send size={18} className="text-blue-600 shrink-0" />
                                    Últimas comunicaciones enviadas
                                </h3>
                                <p className="text-xs text-gray-500 dark:text-gray-400">
                                    Estado de entrega en tiempo real por Email, SMS y WhatsApp
                                </p>
                            </div>
                            <button
                                onClick={() => setVista('informes')}
                                className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1 shrink-0"
                            >
                                Ver historial <ChevronRight size={14} />
                            </button>
                        </div>

                        {cargando ? (
                            <div className="py-12 text-center text-sm text-gray-400">Cargando comunicaciones recientes...</div>
                        ) : alertas.length === 0 ? (
                            <div className="py-12 text-center text-sm text-gray-400">
                                No hay comunicaciones registradas todavía.
                            </div>
                        ) : (
                            <div className="divide-y divide-gray-100 dark:divide-gray-700">
                                {alertas.slice(0, 6).map(alerta => {
                                    const canal = (alerta.canal || 'email').toLowerCase();
                                    const IconoCanal = canal === 'whatsapp' ? Smartphone : canal === 'sms' ? MessageSquare : Mail;
                                    const badgeCanal =
                                        canal === 'whatsapp'
                                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300'
                                            : canal === 'sms'
                                            ? 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/30 dark:text-amber-300'
                                            : 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300';

                                    const fechaStr = new Date(alerta.fecha_enviada || alerta.fecha_creacion).toLocaleString('es-ES', {
                                        day: '2-digit',
                                        month: 'short',
                                        hour: '2-digit',
                                        minute: '2-digit'
                                    });
                                    const tasa = Number(alerta.tasa_exito ?? 100);

                                    return (
                                        <div key={alerta.id} className="py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4 hover:bg-gray-50/70 dark:hover:bg-gray-700/40 px-1 sm:px-2 rounded-lg transition">
                                            <div className="flex items-center gap-3 min-w-0">
                                                <div className={`w-9 h-9 rounded-xl border flex items-center justify-center shrink-0 ${badgeCanal}`}>
                                                    <IconoCanal size={17} />
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate">
                                                        {alerta.nombre_campana || alerta.plantilla_nombre || `Comunicación #${alerta.id}`}
                                                    </p>
                                                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                                        {alerta.plantilla_nombre} · <span className="uppercase font-medium">{canal}</span>
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-4 pl-12 sm:pl-0 shrink-0 text-right">
                                                <div className="text-left sm:text-right">
                                                    <p className="text-xs font-bold text-gray-800 dark:text-gray-200">
                                                        {alerta.destinatarios_exitosos ?? alerta.total_destinatarios}/{alerta.total_destinatarios} entregados
                                                    </p>
                                                    <p className="text-[11px] text-gray-400">{fechaStr}</p>
                                                </div>
                                                <span
                                                    className={`px-2.5 py-1 rounded-full text-xs font-semibold ${
                                                        tasa >= 90
                                                            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300'
                                                            : tasa >= 50
                                                            ? 'bg-amber-50 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300'
                                                            : 'bg-rose-50 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300'
                                                    }`}
                                                >
                                                    {Math.round(tasa)}%
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {/* Barra rápida de directorios / listas */}
                    <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-500 dark:text-gray-400">
                        <span className="flex items-center gap-1.5">
                            <List size={14} className="text-blue-500" />
                            <strong>{listas.length}</strong> listas de distribución configuradas
                        </span>
                        <button
                            onClick={() => setVista('listas')}
                            className="text-blue-600 dark:text-blue-400 font-semibold hover:underline"
                        >
                            Gestionar listas →
                        </button>
                    </div>
                </div>

                {/* Columna Derecha (5 cols): Actividad Reciente + Acciones Pendientes */}
                <div className="lg:col-span-5 space-y-6">
                    {/* Tarjeta: Actividad reciente */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/80 dark:border-gray-700 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-base font-bold text-gray-900 dark:text-gray-100">
                                Actividad reciente
                            </h3>
                            <button
                                onClick={() => setVista('informes')}
                                className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                            >
                                Ver todo →
                            </button>
                        </div>

                        <div className="space-y-3.5">
                            {(logs.length > 0 ? logs.slice(0, 5) : alertas.slice(0, 5)).map((item, idx) => {
                                const esLog = !!item.mensaje;
                                const titulo = esLog
                                    ? item.mensaje
                                    : `${item.plantilla_nombre || 'Alerta enviada'} (${(item.canal || '').toUpperCase()})`;
                                const subtitulo = esLog
                                    ? `${item.usuario_nombre || 'Sistema'} · Módulo ${item.modulo || 'general'}`
                                    : `Enviada a ${item.destinatarios_exitosos || item.total_destinatarios || 0} destinatarios`;
                                const fechaItem = new Date(item.fecha_creacion || item.fecha_enviada || Date.now());
                                const horaItem = fechaItem.toLocaleTimeString('es-ES', { hour: '2-digit', minute: '2-digit' });

                                const tipoColor =
                                    item.tipo === 'error' || item.estado === 'fallida'
                                        ? 'bg-rose-100 text-rose-600 dark:bg-rose-900/40 dark:text-rose-300'
                                        : item.tipo === 'warning'
                                        ? 'bg-amber-100 text-amber-600 dark:bg-amber-900/40 dark:text-amber-300'
                                        : 'bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300';

                                return (
                                    <div key={item.id || idx} className="flex items-start justify-between gap-3">
                                        <div className="flex items-start gap-3 min-w-0">
                                            <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 mt-0.5 ${tipoColor}`}>
                                                {item.tipo === 'error' ? (
                                                    <AlertTriangle size={15} />
                                                ) : item.tipo === 'warning' ? (
                                                    <Bell size={15} />
                                                ) : (
                                                    <CheckCircle2 size={15} />
                                                )}
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-xs font-bold text-gray-800 dark:text-gray-100 truncate">
                                                    {titulo}
                                                </p>
                                                <p className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                                                    {subtitulo}
                                                </p>
                                            </div>
                                        </div>
                                        <span className="text-[11px] font-mono text-gray-400 shrink-0">
                                            {horaItem}
                                        </span>
                                    </div>
                                );
                            })}
                        </div>
                    </div>

                    {/* Tarjeta: Acciones pendientes */}
                    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/80 dark:border-gray-700 p-6">
                        <div className="flex items-center justify-between mb-4">
                            <div className="flex items-center gap-2">
                                <h3 className="text-base font-bold text-gray-900 dark:text-gray-100">
                                    Acciones pendientes
                                </h3>
                                <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-rose-500 text-white">
                                    {pendientesAprobacion.length + (metricas.enviosConFallo > 0 ? 1 : 0)}
                                </span>
                            </div>
                            {usuario?.rol !== 'operador' && (
                                <button
                                    onClick={() => setVista('aprobaciones')}
                                    className="text-xs font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                                >
                                    Ver todas →
                                </button>
                            )}
                        </div>

                        <div className="space-y-3">
                            {pendientesAprobacion.length > 0 ? (
                                pendientesAprobacion.slice(0, 2).map(p => (
                                    <div key={p.id} className="p-3 rounded-xl bg-amber-50/70 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 flex items-center justify-between gap-3">
                                        <div className="flex items-center gap-3 min-w-0">
                                            <div className="w-8 h-8 rounded-full bg-amber-500 text-white flex items-center justify-center shrink-0">
                                                <AlertTriangle size={15} />
                                            </div>
                                            <div className="min-w-0">
                                                <p className="text-xs font-bold text-gray-900 dark:text-gray-100 truncate">
                                                    Aprobar: {p.plantilla_nombre || `Alerta #${p.id}`}
                                                </p>
                                                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                                    Canal {p.canal?.toUpperCase()} · {p.total_destinatarios} destinatarios
                                                </p>
                                            </div>
                                        </div>
                                        <button
                                            onClick={() => setVista('aprobaciones')}
                                            className="px-3 py-1.5 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold shrink-0"
                                        >
                                            Revisar
                                        </button>
                                    </div>
                                ))
                            ) : (
                                <div className="p-3 rounded-xl bg-emerald-50/60 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-3">
                                        <div className="w-8 h-8 rounded-full bg-emerald-500 text-white flex items-center justify-center shrink-0">
                                            <CheckCircle2 size={16} />
                                        </div>
                                        <div>
                                            <p className="text-xs font-bold text-gray-900 dark:text-gray-100">
                                                Cola de aprobaciones al día
                                            </p>
                                            <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                                No hay alertas esperando autorización
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={iniciarFlujoAlerta}
                                        className="px-3 py-1.5 rounded-lg bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-xs font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-50"
                                    >
                                        Nueva
                                    </button>
                                </div>
                            )}

                            {/* Segunda fila rápida: Estado de canales y emisores */}
                            <div className="p-3 rounded-xl bg-gray-50 dark:bg-gray-700/40 border border-gray-200 dark:border-gray-700 flex items-center justify-between gap-3">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 flex items-center justify-center shrink-0">
                                        <Activity size={16} />
                                    </div>
                                    <div>
                                        <p className="text-xs font-bold text-gray-900 dark:text-gray-100">
                                            Configuración de canales y remitentes
                                        </p>
                                        <p className="text-[11px] text-gray-500 dark:text-gray-400">
                                            Email, SMS y WhatsApp Business verificados
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => setVista(usuario?.rol === 'superadmin' ? 'clientes' : 'informes')}
                                    className="px-3 py-1.5 rounded-lg bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-xs font-semibold text-gray-700 dark:text-gray-200 hover:bg-gray-100 shrink-0"
                                >
                                    Ver
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* FILA INFERIOR DE 3 GRÁFICAS (IGUAL QUE EN LA IMAGEN DE REFERENCIA) */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* 1. Donut Chart: Tipos de alertas */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/80 dark:border-gray-700 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-base font-bold text-gray-900 dark:text-gray-100">
                            Tipos de alertas
                        </h3>
                        <select
                            value={periodoDias}
                            onChange={e => setPeriodoDias(Number(e.target.value))}
                            className="text-xs border border-gray-200 dark:border-gray-600 rounded-lg px-2.5 py-1 bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-200 outline-none"
                        >
                            <option value={30}>Últimos 30 días</option>
                            <option value={90}>Últimos 90 días</option>
                            <option value={365}>Último año</option>
                        </select>
                    </div>

                    <div className="flex flex-col sm:flex-row items-center gap-6 pt-2">
                        {/* SVG Donut */}
                        <div className="relative w-36 h-36 flex items-center justify-center shrink-0">
                            <svg className="w-36 h-36 -rotate-90" viewBox="0 0 140 140">
                                <circle
                                    cx="70"
                                    cy="70"
                                    r={radioDonut}
                                    fill="transparent"
                                    stroke="#E5E7EB"
                                    strokeWidth="16"
                                />
                                {distribucionTipos.items.map((it, i) => {
                                    const longitud = (it.pct / 100) * circunferencia;
                                    const dashArray = `${longitud} ${circunferencia}`;
                                    const dashOffset = -offsetAcumulado;
                                    offsetAcumulado += longitud;
                                    if (it.count === 0) return null;
                                    return (
                                        <circle
                                            key={i}
                                            cx="70"
                                            cy="70"
                                            r={radioDonut}
                                            fill="transparent"
                                            stroke={it.color}
                                            strokeWidth="16"
                                            strokeDasharray={dashArray}
                                            strokeDashoffset={dashOffset}
                                            strokeLinecap="butt"
                                        />
                                    );
                                })}
                            </svg>
                            <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
                                <span className="text-2xl font-extrabold text-gray-900 dark:text-gray-100 leading-none">
                                    {distribucionTipos.total}
                                </span>
                                <span className="text-[11px] text-gray-500 dark:text-gray-400 mt-1">
                                    comunicaciones
                                </span>
                            </div>
                        </div>

                        {/* Leyenda */}
                        <div className="flex-1 w-full space-y-2.5 text-xs">
                            {distribucionTipos.items.map((it, i) => (
                                <div key={i} className="flex items-center justify-between gap-2">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <span
                                            className="w-3 h-3 rounded-full shrink-0"
                                            style={{ backgroundColor: it.color }}
                                        />
                                        <span className="text-gray-700 dark:text-gray-300 truncate">{it.label}</span>
                                    </div>
                                    <span className="font-bold text-gray-900 dark:text-gray-100 shrink-0">
                                        {it.count} <span className="text-gray-400 font-normal">({it.pct}%)</span>
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* 2. Barras de progreso: Canales de comunicación */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/80 dark:border-gray-700 p-6">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-base font-bold text-gray-900 dark:text-gray-100">
                            Canales de comunicación
                        </h3>
                        <span className="text-xs text-gray-400 font-medium">
                            Últimos {periodoDias} días
                        </span>
                    </div>

                    <div className="space-y-4 pt-1">
                        {estadisticasCanales.map(c => {
                            const Icono = c.icon;
                            return (
                                <div key={c.id} className="space-y-1.5">
                                    <div className="flex items-center justify-between text-xs">
                                        <span className="flex items-center gap-2 font-semibold text-gray-700 dark:text-gray-200">
                                            <Icono size={15} className={c.colorText} />
                                            {c.label}
                                        </span>
                                        <div className="flex items-center gap-3">
                                            <span className="font-bold text-gray-900 dark:text-gray-100">
                                                {c.tasaEntrega}% éxito
                                            </span>
                                            <span className="text-gray-400 font-mono w-16 text-right">
                                                {c.enviados.toLocaleString('es-ES')} env.
                                            </span>
                                        </div>
                                    </div>
                                    <div className="w-full h-2.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                                        <div
                                            className={`h-full rounded-full transition-all duration-500 ${c.colorBar}`}
                                            style={{ width: `${c.anchoBarra}%` }}
                                        />
                                    </div>
                                    <div className="text-[11px] text-gray-400 flex justify-between">
                                        <span>Suscritos activos: {c.suscritos.toLocaleString('es-ES')}</span>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* 3. Gráfica de línea / área: Población alcanzada */}
                <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/80 dark:border-gray-700 p-6 flex flex-col justify-between">
                    <div className="flex items-center justify-between mb-2">
                        <div>
                            <h3 className="text-base font-bold text-gray-900 dark:text-gray-100">
                                Población alcanzada
                            </h3>
                            <p className="text-xs text-gray-400">
                                Evolución acumulada de destinatarios
                            </p>
                        </div>
                        <div className="text-right">
                            <span className="text-lg font-extrabold text-blue-600 dark:text-blue-400">
                                {metricas.poblacionAlcanzada.toLocaleString('es-ES')}
                            </span>
                            <span className="block text-[10px] text-gray-400">personas alcanzadas</span>
                        </div>
                    </div>

                    <div className="pt-2">
                        <svg viewBox={`0 0 ${anchoSvg} ${altoSvg}`} className="w-full h-36 overflow-visible">
                            <defs>
                                <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="0%" stopColor="#2563EB" stopOpacity="0.28" />
                                    <stop offset="100%" stopColor="#2563EB" stopOpacity="0.0" />
                                </linearGradient>
                            </defs>

                            {/* Líneas guía horizontales */}
                            {[0.25, 0.5, 0.75].map((ratio, idx) => (
                                <line
                                    key={idx}
                                    x1={padX}
                                    x2={anchoSvg - padX}
                                    y1={padY + ratio * (altoSvg - padY * 2)}
                                    y2={padY + ratio * (altoSvg - padY * 2)}
                                    stroke="#E5E7EB"
                                    strokeDasharray="3 3"
                                    strokeWidth="1"
                                />
                            ))}

                            {/* Área sombreada */}
                            {areaPoints && <polygon points={areaPoints} fill="url(#areaGrad)" />}

                            {/* Línea principal */}
                            {polylinePoints && (
                                <polyline
                                    fill="none"
                                    stroke="#2563EB"
                                    strokeWidth="3"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    points={polylinePoints}
                                />
                            )}

                            {/* Puntos y etiquetas eje X */}
                            {coords.map((c, idx) => (
                                <g key={idx}>
                                    <circle
                                        cx={c.x}
                                        cy={c.y}
                                        r={idx === coords.length - 1 ? 5 : 3}
                                        fill={idx === coords.length - 1 ? '#2563EB' : '#FFFFFF'}
                                        stroke="#2563EB"
                                        strokeWidth="2.5"
                                    />
                                    {(idx === 0 || idx === Math.floor(coords.length / 2) || idx === coords.length - 1) && (
                                        <text
                                            x={c.x}
                                            y={altoSvg - 3}
                                            textAnchor="middle"
                                            className="fill-gray-400 text-[10px]"
                                        >
                                            {c.label}
                                        </text>
                                    )}
                                </g>
                            ))}
                        </svg>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;
